import { useMemo } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { getAnalysisPanelTheme } from '../theme/themeStyles';
import type { FarmForecastAlertRecord } from '../utils/farmMobileAlerts';
import {
  forecastAlertsToChartPoints,
  formatForecastChartDate,
  formatForecastValue,
  MIN_FORECAST_TREND_CHART_POINTS,
} from '../utils/farmMobileAlerts';

type FarmForecastTrendChartProps = {
  title: string;
  alerts: FarmForecastAlertRecord[];
  valueKind: 'ndvi' | 'ndmi' | 'rain_mm' | 'rain_pop' | 'temp_c' | 'weather';
  compact?: boolean;
};

export default function FarmForecastTrendChart({
  title,
  alerts,
  valueKind,
  compact = false,
}: FarmForecastTrendChartProps) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);

  const points = useMemo(() => forecastAlertsToChartPoints(alerts), [alerts]);

  const chart = useMemo(() => {
    if (points.length < MIN_FORECAST_TREND_CHART_POINTS) return null;

    const width = compact ? 280 : 320;
    const height = compact ? 100 : 120;
    const padL = 36;
    const padR = 12;
    const padT = 12;
    const padB = 28;
    const innerW = width - padL - padR;
    const innerH = height - padT - padB;

    const values = points.map((p) => p.value);
    let minV = Math.min(...values);
    let maxV = Math.max(...values);
    if (minV === maxV) {
      minV -= valueKind === 'rain_mm' || valueKind === 'weather' ? 5 : valueKind === 'rain_pop' ? 10 : 0.05;
      maxV += valueKind === 'rain_mm' || valueKind === 'weather' ? 5 : valueKind === 'rain_pop' ? 10 : 0.05;
    }
    const range = maxV - minV || 1;

    const coords = points.map((p, i) => {
      const x = padL + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
      const y = padT + innerH - ((p.value - minV) / range) * innerH;
      return { x, y, ...p };
    });

    const linePath = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
    const areaPath = `${linePath} L${coords[coords.length - 1].x.toFixed(1)},${(padT + innerH).toFixed(1)} L${coords[0].x.toFixed(1)},${(padT + innerH).toFixed(1)} Z`;

    const stroke =
      valueKind === 'ndmi'
        ? isDark
          ? '#38bdf8'
          : '#0284c7'
        : valueKind === 'rain_mm' || valueKind === 'weather'
          ? isDark
            ? '#60a5fa'
            : '#2563eb'
          : valueKind === 'rain_pop'
            ? isDark
              ? '#38bdf8'
              : '#0284c7'
            : isDark
            ? '#4ade80'
            : '#008540';

    const fill = isDark ? `${stroke}33` : `${stroke}22`;

    return { width, height, coords, linePath, areaPath, minV, maxV, stroke, fill, padL, padT, innerH };
  }, [points, compact, isDark, valueKind]);

  if (!chart || points.length < MIN_FORECAST_TREND_CHART_POINTS) return null;

  return (
    <div className={`rounded-xl border ${ap.cardSoft} ${compact ? 'p-2' : 'p-3'}`}>
      <div className={`mb-2 flex items-center justify-between gap-2 ${compact ? 'text-[10px]' : 'text-xs'}`}>
        <span className={`font-semibold ${ap.heading}`}>{title}</span>
        <span className={ap.faint}>Forecast trend</span>
      </div>
      <svg
        viewBox={`0 0 ${chart.width} ${chart.height}`}
        className="w-full max-w-full"
        role="img"
        aria-label={`${title} forecast trend chart`}
      >
        <line
          x1={chart.padL}
          y1={chart.padT + chart.innerH}
          x2={chart.width - 12}
          y2={chart.padT + chart.innerH}
          stroke={isDark ? '#4b5563' : '#d1d5db'}
          strokeWidth="1"
        />
        <path d={chart.areaPath} fill={chart.fill} />
        <path d={chart.linePath} fill="none" stroke={chart.stroke} strokeWidth="2" strokeLinejoin="round" />
        {chart.coords.map((c) => (
          <g key={c.date}>
            <circle cx={c.x} cy={c.y} r={compact ? 3 : 3.5} fill={chart.stroke} />
            <text
              x={c.x}
              y={chart.height - 6}
              textAnchor="middle"
              fontSize={compact ? 8 : 9}
              fill={isDark ? '#9ca3af' : '#6b7280'}
            >
              {formatForecastChartDate(c.date)}
            </text>
            <text
              x={c.x}
              y={c.y - 6}
              textAnchor="middle"
              fontSize={compact ? 8 : 9}
              fontWeight="600"
              fill={isDark ? '#e5e7eb' : '#374151'}
            >
              {formatForecastValue(c.value, valueKind)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
